/**
 * AyudaWhatsApp — botón flotante inferior-derecha con menú de opciones.
 * Todas las opciones abren wa.me con mensaje prellenado hacia el número del dueño.
 *
 * Opciones:
 *   - Capacitación
 *   - Facturación / Pagos
 *   - Reportar problema
 *   - Configurar impresora
 *   - Activar complemento
 *   - Ver tour del sistema (dispara tour local, no abre WhatsApp)
 *   - Otra duda
 *
 * El número WhatsApp es una constante configurable global.
 */
import { useState } from 'react';
import { MessageCircle, X, GraduationCap, CreditCard, Bug, Printer, ShoppingBag, PlayCircle, HelpCircle } from 'lucide-react';
import { useAuth } from '@/contexto/AuthContexto';

// Número de soporte oficial (editable en el futuro desde AdminPanel > Configuración global)
const WHATSAPP_SOPORTE = '5299949 63413'.replace(/\s/g, '');

function abrirWhatsApp(mensaje) {
  const url = `https://wa.me/${WHATSAPP_SOPORTE}?text=${encodeURIComponent(mensaje)}`;
  window.open(url, '_blank');
}

export default function AyudaWhatsApp() {
  const { usuario } = useAuth();
  const [abierto, setAbierto] = useState(false);

  if (!usuario || usuario.rol === 'super_admin') return null;

  const contexto = `Hola, soy ${usuario.nombre || ''} del negocio "${usuario.tenant_nombre || ''}" (${usuario.email}).`;

  const opciones = [
    {
      icon: GraduationCap, color: 'text-emerald-400',
      label: 'Solicitar capacitación',
      testid: 'ayuda-capacitacion',
      accion: () => abrirWhatsApp(`${contexto} Me gustaría agendar una capacitación para aprender a usar mejor el sistema.`),
    },
    {
      icon: PlayCircle, color: 'text-cyan-400',
      label: 'Ver tour del sistema',
      testid: 'ayuda-tour',
      accion: () => {
        // Dispara el tour local sin abrir WhatsApp
        window.dispatchEvent(new CustomEvent('iniciar-tour'));
        setAbierto(false);
      },
    },
    {
      icon: ShoppingBag, color: 'text-amber-400',
      label: 'Activar un complemento',
      testid: 'ayuda-complemento',
      accion: () => abrirWhatsApp(`${contexto} Quiero información sobre los complementos (ticket térmico, fotos de recepción, evidencias). ¿Cuál me recomiendan?`),
    },
    {
      icon: CreditCard, color: 'text-blue-400',
      label: 'Duda de facturación o pago',
      testid: 'ayuda-facturacion',
      accion: () => abrirWhatsApp(`${contexto} Tengo una duda sobre mi facturación / pago.`),
    },
    {
      icon: Printer, color: 'text-purple-400',
      label: 'Configurar impresora',
      testid: 'ayuda-impresora',
      accion: () => abrirWhatsApp(`${contexto} Necesito ayuda para configurar mi impresora térmica en el sistema.`),
    },
    {
      icon: Bug, color: 'text-red-400',
      label: 'Reportar un problema',
      testid: 'ayuda-bug',
      accion: () => abrirWhatsApp(`${contexto} Encontré un problema en el sistema:\n[Describe brevemente qué pasa y en qué pantalla]`),
    },
    {
      icon: HelpCircle, color: 'text-slate-400',
      label: 'Otra duda',
      testid: 'ayuda-otro',
      accion: () => abrirWhatsApp(`${contexto} Tengo una duda:`),
    },
  ];

  return (
    <>
      {/* Overlay para cerrar al hacer clic afuera */}
      {abierto && (
        <div className="fixed inset-0 z-40" onClick={() => setAbierto(false)} />
      )}

      {/* Menú flotante */}
      {abierto && (
        <div className="fixed bottom-24 right-6 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl w-72 overflow-hidden animate-in fade-in slide-in-from-bottom-4"
          data-testid="ayuda-menu">
          <div className="bg-emerald-500/10 border-b border-emerald-500/30 px-4 py-3">
            <p className="text-sm font-semibold text-emerald-300">¿Cómo podemos ayudarte?</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Elige una opción y te conectamos vía WhatsApp</p>
          </div>
          <div className="divide-y divide-slate-800 max-h-[70vh] overflow-y-auto">
            {opciones.map((op, i) => (
              <button key={i} onClick={() => { op.accion(); if (op.testid !== 'ayuda-tour') setAbierto(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/50 transition text-left"
                data-testid={op.testid}>
                <op.icon className={`w-5 h-5 shrink-0 ${op.color}`} />
                <span className="text-sm text-slate-200">{op.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Botón flotante */}
      <button onClick={() => setAbierto(!abierto)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg hover:shadow-emerald-500/50 hover:scale-105 transition-all flex items-center justify-center"
        data-testid="ayuda-btn"
        title="Ayuda vía WhatsApp">
        {abierto ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
}
